import React, { KeyboardEvent, useContext, useState } from "react";

import {
  Alert,
  Box,
  Button,
  Link,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  forgotPassword,
  login,
  forgotUserName,
} from "../../api/ProcurementClient";
import { AppContext } from "../../AppContextProvider";
import { AxiosError } from "axios";
import ConfirmDialog from "../ConfirmDialog";
import { SessionInfo } from "../../models/SessionInfo";

enum ForgottenCredential {
  USERNAME,
  PWD,
}
export const CredentialsPanel: React.FC = () => {
  const [userName, setUserName] = useState("");

  const {
    setUser,
    setUsers,
    setCustomers,
    setSnackbarMsg,
    setRfqs,
    setVendors,
    setManufacturers,
    setPartNumberTypes,
    setFeeTypes,
    setNoteTypes,
    setDeliveryMethods,
    setDeliverySpeeds,
    setRfqStatuses,
  } = useContext(AppContext);

  const [password, setPassword] = useState<string>("");
  const [errorMsg, setErrorMsg] = useState<string>();
  const [forgottenCredential, setForgottenCredential] =
    useState<ForgottenCredential>();
  const [email, setEmail] = useState<string>("");

  const handleForgotCancelled = () => {
    setForgottenCredential(undefined);
    setEmail("");
  };

  const handleLogin = async () => {
    try {
      const loginResponse = await login(userName, password);
      setUser(loginResponse.user);
      setUsers(loginResponse.users);
      setCustomers(loginResponse.customers);
      setRfqs(loginResponse.requestForQuotes);
      setVendors(loginResponse.vendors);
      setManufacturers(loginResponse.manufacturers);
      setPartNumberTypes(loginResponse.partNumberTypes ?? []);
      setFeeTypes(loginResponse.feeTypes ?? []);
      setNoteTypes(loginResponse.noteTypes ?? []);
      setDeliveryMethods(loginResponse.deliveryMethods ?? []);
      setDeliverySpeeds(loginResponse.deliverySpeeds ?? []);
      setRfqStatuses(loginResponse.rfqStatuses ?? []);

      localStorage.setItem("jwtToken", loginResponse.jwtToken);
    } catch (error) {
      const axiosError = error as AxiosError;
      if (axiosError.response?.status === 401) {
        setErrorMsg("Invalid username or password");
      } else {
        setErrorMsg(
          "Unable to login: " +
          (axiosError.response?.data?.toString() ??
            axiosError.response?.statusText ??
            "Unknown server error")
        );
      }
    }
  };

  const handleCloseSnackbar = async () => {
    setErrorMsg(undefined);
  };

  const handleKeyPressed = async (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === "Enter") {
      await handleLogin();
    }
  };

  const handleSendForgotPwdEmail = async () => {
    try {
      await forgotPassword(userName);
      setSnackbarMsg("Email has been sent.");
      setForgottenCredential(undefined);
    } catch (error) {
      setForgottenCredential(undefined);
      const axiosError = error as AxiosError;
      setErrorMsg(
        "Unable to send email: " +
        (axiosError.response?.data?.toString() ??
          axiosError.response?.statusText ??
          "Unknown server error")
      );
    }
  };

  const handleSendForgotUsernameEmail = async () => {
    try {
      await forgotUserName(email);
      setSnackbarMsg("Email has been sent.");
      setForgottenCredential(undefined);
    } catch (error) {
      setForgottenCredential(undefined);
      const axiosError = error as AxiosError;
      setErrorMsg(
        "Unable to send email: " +
        (axiosError.response?.data?.toString() ??
          axiosError.response?.statusText ??
          "Unknown server error")
      );
    }
  };

  return (
    <Stack direction="column" gap={2}>
      <TextField
        fullWidth
        required
        label="Username"
        variant="standard"
        value={userName}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
          setUserName(event.target.value)
        }
      />
      <TextField
        fullWidth
        required
        type="Password"
        label="Password"
        variant="standard"
        value={password}
        onKeyUp={handleKeyPressed}
        onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
          setPassword(event.target.value)
        }
      />

      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "center",
          gap: 3,
        }}
      >
        <Link
          component="button"
          disabled={!userName.length}
          variant="body2"
          onClick={() => setForgottenCredential(ForgottenCredential.PWD)}
        >
          Forgot Password
        </Link>
        <Link
          component="button"
          variant="body2"
          onClick={() => setForgottenCredential(ForgottenCredential.USERNAME)}
        >
          Forgot Username
        </Link>
      </Box>
      <Button
        variant="contained"
        color="primary"
        disabled={!userName.length || !password.length}
        onClick={handleLogin}
        sx={{
          marginTop: "50px",
        }}
      >
        Login
      </Button>
      <Snackbar
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
        open={errorMsg ? true : false}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
      >
        <Alert severity={"error"} onClose={handleCloseSnackbar}>
          {errorMsg}
        </Alert>
      </Snackbar>
      {forgottenCredential === ForgottenCredential.USERNAME ? (
        <ConfirmDialog
          open={true}
          onOk={() => {
            return;
          }}
          onCancel={handleForgotCancelled}
        >
          <TextField
            fullWidth
            required
            label="Email Associated With My Account"
            variant="standard"
            value={""}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setEmail(event.target.value)
            }
          />
        </ConfirmDialog>
      ) : (
        <></>
      )}
      {forgottenCredential === ForgottenCredential.USERNAME ? (
        <ConfirmDialog
          open={true}
          title="Forgot Username"
          isOkDisabled={!email.length}
          onOk={handleSendForgotUsernameEmail}
          onCancel={handleForgotCancelled}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography>
              We&apos;ll look up your login information using your email address
              and send you your username.
            </Typography>

            <TextField
              fullWidth
              required
              label="Email"
              variant="standard"
              value={email}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setEmail(event.target.value)
              }
            />
          </Box>
        </ConfirmDialog>
      ) : (
        <></>
      )}
      {forgottenCredential === ForgottenCredential.PWD ? (
        <ConfirmDialog
          open={true}
          title="Forgot Password"
          onOk={handleSendForgotPwdEmail}
          onCancel={handleForgotCancelled}
        >
          <Typography>
            We&apos;ll lookup your email address and send instructions on how to
            reset your password.
          </Typography>
        </ConfirmDialog>
      ) : (
        <></>
      )}
    </Stack>
  );
};

export interface LoginResponse extends SessionInfo {
  jwtToken: string;
}

const LoginPanel: React.FC = () => {
  return (
    <Box
      sx={{
        flexBasis: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        alignItems: "center",
        gap: 3,
        height: "100%",
      }}
    >
      <Box sx={{ width: "290px", position: "relative" }}>
        <Typography
          textAlign="right"
          fontSize="50px"
          fontWeight="bold"
          sx={{
            color: "#333333",
            textShadow: "2px 2px 8px rgba(0, 0, 0, 0.6)",
            fontWeight: 900,
            fontFamily: "'Segoe UI', 'Roboto', 'Arial', sans-serif",
          }}
        >
          Rocket City Supply
        </Typography>
        <img
          src="/RocketCityLogo.png"
          style={{
            position: "absolute",
            bottom: "0px",
            left: "20px",
            width: "90px",
            height: "auto",
          }}
          alt="logo"
        />
      </Box>
      <Box sx={{ width: "400px" }}>
        <CredentialsPanel />
      </Box>
    </Box>
  );
};
export default LoginPanel;
